import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FilehandlerService } from '@app/core/services/file/filehandler.service';
import { SessionService } from '@app/core/services/session/session.service';
import { User } from '@app/shared/models/user.interface';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AvatarDialogComponent } from './avatar-dialog/avatar-dialog.component';

export interface AvatarView {
  name: string;
  surname: string;
  image?: string;
  id?: string;
}

@Component({
  selector: 'vfs-avatar-upload',
  templateUrl: './avatar-upload.component.html',
  styleUrls: ['./avatar-upload.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AvatarUploadComponent implements OnInit, OnChanges {
  @HostBinding() class = 'vfs-avatar';
  @Input() view: AvatarView;
  @Input() size: 'xl' | 'l' | 'm' | 's' | 'xs' = 'l';
  @Input() disabled: boolean;
  @Input() preview: string;
  @Input() publicImage = false;
  @Input() ofLoggedUser = false;

  @Output() avatarClick: EventEmitter<Event> = new EventEmitter();
  @Output() avatarChanged = new EventEmitter();

  mouseEnterImage = false;
  user$: Observable<User>;
  imageUrl$: Observable<string>;

  constructor(
    protected sessionService: SessionService,
    protected translate?: TranslateService,
    private dialog?: MatDialog,
    protected fileHandlerService?: FilehandlerService
  ) {}

  ngOnInit() {
    this.user$ = this.sessionService.userSubject$.asObservable();
    this.loadImage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.view && !changes.view.firstChange && changes.view.currentValue.id !== changes.view.previousValue.id) {
      this.loadImage();
    }
  }

  loadImage(): void {
    this.imageUrl$ = this.ofLoggedUser
      ? this.user$.pipe(
          switchMap(user =>
            this.fileHandlerService
              .getFileUrl(user && user.image ? user.image : '', this.publicImage)
              .pipe(map(result => result.url))
          )
        )
      : this.fileHandlerService.getFileUrl(this.view.image, this.publicImage).pipe(map(result => result.url));
  }

  showModal() {
    this.dialog
      .open(AvatarDialogComponent, {
        panelClass: 'vfs-avatar-dialog-container',
        data: {
          view: this.view,
          userInitials: this.view.name && this.view.surname ? this.userInitials : ''
        }
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.avatarChanged.emit(result);
        }
      });
  }

  onAvatarClick(evt: Event) {
    if (this.avatarClick.observers.length >= 1) {
      this.avatarClick.emit(evt);
    } else {
      this.showModal();
    }
  }

  get userInitials(): string {
    let name = this.view.name ? this.view.name : '';
    let surname = this.view.surname ? this.view.surname : '';
    let surnameToShow = surname.split(' ');
    let initials = (name.charAt(0) + surnameToShow[surnameToShow.length - 1].charAt(0)).toUpperCase();
    return initials;
  }

  /**
   * Get the size of the image and the spinner.
   * @param spinnerParam with parameter 'diameter' or 'stroke' sets the spinner size, without, the avatar class.
   */
  getSizeClasses(spinnerParam?: string) {
    // FIXME: Rethink this
    switch (this.size.toLowerCase()) {
      case 'xl':
        return spinnerParam ? (spinnerParam === 'diameter' ? 90 : 8) : 'avatar-size-xl';
      case 'l':
        return spinnerParam ? (spinnerParam === 'diameter' ? 74 : 6) : 'avatar-size-l';
      case 'm':
        return spinnerParam ? (spinnerParam === 'diameter' ? 54 : 5) : 'avatar-size-m';
      case 's':
        return spinnerParam ? (spinnerParam === 'diameter' ? 38 : 4) : 'avatar-size-s';
      case 'xs':
        return spinnerParam ? (spinnerParam === 'diameter' ? 30 : 3) : 'avatar-size-xs';
      default:
        break;
    }
  }
}
